import React, { useState } from "react";
import { useTranslation } from "react-i18next"; // Import the hook for translations
import { useLocation } from "react-router-dom"; // Import to get current path
import "./Header.css";
import logo from "./logo.png";

import enTranslations from "./en.json"; // Import English translations
import deTranslations from "./de.json"; // Import German translations

const Header = () => {
  const { t, i18n } = useTranslation(); // Use translation hook
  const location = useLocation(); // Get current location

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  // Add the translations for this component
  i18n.addResourceBundle("en", "header", enTranslations, true, true);
  i18n.addResourceBundle("de", "header", deTranslations, true, true);

  // Helper function to generate language-aware URLs
  const getLangPath = (path) => {
    const currentLang = i18n.language;
    if (currentLang === "en") {
      return `/en${path}`;
    } else if (currentLang === "de" && path !== "/") {
      return `/de${path}`;
    }
    return path === "/" ? "/" : path;
  };

  // Helper function for language change with URL update
  const changeLanguageWithRedirect = (newLang) => {
    const currentPath = location.pathname;
    let newPath = "/";

    // Remove current language prefix if exists
    const pathWithoutLang = currentPath.replace(/^\/(en|de)/, "") || "/";

    // Add new language prefix
    if (newLang === "en") {
      newPath = `/en${pathWithoutLang}`;
    } else if (newLang === "de") {
      newPath = pathWithoutLang === "/" ? "/" : `/de${pathWithoutLang}`;
    }

    // Change language and navigate
    i18n.changeLanguage(newLang);
    window.location.href = newPath;
  };

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      setMobileMenuOpen(false);
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    } else {
      setMobileMenuOpen(true);
    }
  };

  // Normalize the current language to ensure only 'en' or 'de' is shown
  const currentLanguage = i18n.language.split("-")[0]; // Extract 'en' or 'de'
  const availableLanguages = currentLanguage === "en" ? "de" : "en"; // Switch between 'en' and 'de'

  return (
    <header className="header">
      <nav className="navbar">
        <div className="logo-container">
          <a
            href={getLangPath("/")}
            className="logo-link focus-highlight"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.location.href = getLangPath("/");
              }
            }}
          >
            <img src={logo} alt={t("header:logo_alt")} className="logo" />
          </a>
        </div>
        <ul
          className={`nav-links ${
            isMobileMenuOpen || isAnimating ? "mobile-open" : ""
          }`}
        >
          <li>
            <a
              href={getLangPath("/")}
              className="focus-highlight"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  window.location.href = getLangPath("/");
                }
              }}
            >
              {t("header:menu_home")}
            </a>
          </li>
          <li>
            <a
              href={getLangPath("/contact")}
              className="focus-highlight"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  window.location.href = getLangPath("/contact");
                }
              }}
            >
              {t("header:menu_contact")}
            </a>
          </li>

          {/* Language Switcher grouped with Home and Contact */}
          <li className="menu">
            <a
              href="#"
              className="focus-highlight"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                }
              }}
            >
              {currentLanguage.toUpperCase()}
            </a>
            <ul className="dropdown-language">
              <li>
                <a
                  href="#"
                  className="focus-highlight"
                  tabIndex={0}
                  onClick={() => changeLanguageWithRedirect(availableLanguages)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      changeLanguageWithRedirect(availableLanguages);
                    }
                  }}
                >
                  {availableLanguages.toUpperCase()}
                </a>
              </li>
            </ul>
          </li>

          <div className="divider"></div>

          <li
            className={`menu ${!isAnimating ? "visible" : ""}`}
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <a
              href="#"
              className="focus-highlight"
              tabIndex={0}
              onFocus={() => setDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDropdownOpen(!isDropdownOpen);
                  // Focus first dropdown item when opened
                  if (!isDropdownOpen) {
                    setTimeout(() => {
                      const firstDropdownLink =
                        e.target.parentElement.querySelector(".dropdown a");
                      if (firstDropdownLink) firstDropdownLink.focus();
                    }, 0);
                  }
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setDropdownOpen(true);
                  setTimeout(() => {
                    const firstDropdownLink =
                      e.target.parentElement.querySelector(".dropdown a");
                    if (firstDropdownLink) firstDropdownLink.focus();
                  }, 0);
                } else if (e.key === "Escape") {
                  setDropdownOpen(false);
                }
              }}
            >
              {t("header:menu_portfolio")}
            </a>
            <ul className={`dropdown ${isDropdownOpen ? "show" : ""}`}>
              <li>
                <a
                  href={getLangPath("/buy&rent")}
                  className="focus-highlight"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDropdownOpen(false);
                      window.location.href = getLangPath("/buy&rent");
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      const nextLink =
                        e.target.parentElement.nextElementSibling?.querySelector(
                          "a"
                        );
                      if (nextLink) nextLink.focus();
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      const portfolioLink = e.target
                        .closest(".menu")
                        .querySelector('a[href="#"]');
                      if (portfolioLink) portfolioLink.focus();
                    } else if (e.key === "Escape") {
                      setDropdownOpen(false);
                      const portfolioLink = e.target
                        .closest(".menu")
                        .querySelector('a[href="#"]');
                      if (portfolioLink) portfolioLink.focus();
                    }
                  }}
                  onBlur={(e) => {
                    setTimeout(() => {
                      if (
                        !e.target
                          .closest(".menu")
                          .contains(document.activeElement)
                      ) {
                        setDropdownOpen(false);
                      }
                    }, 0);
                  }}
                >
                  {t("header:menu_buy_rent")}
                </a>
              </li>
              <li>
                <a
                  href={getLangPath("/repair&restore")}
                  className="focus-highlight"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDropdownOpen(false);
                      window.location.href = getLangPath("/repair&restore");
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      const nextLink =
                        e.target.parentElement.nextElementSibling?.querySelector(
                          "a"
                        );
                      if (nextLink) nextLink.focus();
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      const prevLink =
                        e.target.parentElement.previousElementSibling?.querySelector(
                          "a"
                        );
                      if (prevLink) prevLink.focus();
                    } else if (e.key === "Escape") {
                      setDropdownOpen(false);
                      const portfolioLink = e.target
                        .closest(".menu")
                        .querySelector('a[href="#"]');
                      if (portfolioLink) portfolioLink.focus();
                    }
                  }}
                  onBlur={(e) => {
                    setTimeout(() => {
                      if (
                        !e.target
                          .closest(".menu")
                          .contains(document.activeElement)
                      ) {
                        setDropdownOpen(false);
                      }
                    }, 0);
                  }}
                >
                  {t("header:menu_repair_restore")}
                </a>
              </li>
              <li>
                <a
                  href={getLangPath("/analyse&document")}
                  className="focus-highlight"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDropdownOpen(false);
                      window.location.href = getLangPath("/analyse&document");
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      const prevLink =
                        e.target.parentElement.previousElementSibling?.querySelector(
                          "a"
                        );
                      if (prevLink) prevLink.focus();
                    } else if (e.key === "Escape") {
                      setDropdownOpen(false);
                      const portfolioLink = e.target
                        .closest(".menu")
                        .querySelector('a[href="#"]');
                      if (portfolioLink) portfolioLink.focus();
                    }
                  }}
                  onBlur={(e) => {
                    setTimeout(() => {
                      if (
                        !e.target
                          .closest(".menu")
                          .contains(document.activeElement)
                      ) {
                        setDropdownOpen(false);
                      }
                    }, 0);
                  }}
                >
                  {t("header:menu_analyse_document")}
                </a>
              </li>
            </ul>
          </li>

          <div
            className={`mobile-portfolio ${
              isMobileMenuOpen || isAnimating ? "visible" : ""
            }`}
          >
            <li>
              <a
                href={getLangPath("/buy&rent")}
                className="focus-highlight"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    window.location.href = getLangPath("/buy&rent");
                  }
                }}
              >
                {t("header:menu_buy_rent")}
              </a>
            </li>
            <li>
              <a
                href={getLangPath("/repair&restore")}
                className="focus-highlight"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    window.location.href = getLangPath("/repair&restore");
                  }
                }}
              >
                {t("header:menu_repair_restore")}
              </a>
            </li>
            <li>
              <a
                href={getLangPath("/analyse&document")}
                className="focus-highlight"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    window.location.href = getLangPath("/analyse&document");
                  }
                }}
              >
                {t("header:menu_analyse_document")}
              </a>
            </li>
          </div>
        </ul>

        <div
          className={`mobile-language-toggle focus-highlight`}
          tabIndex={0}
          onClick={() => changeLanguageWithRedirect(availableLanguages)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              changeLanguageWithRedirect(availableLanguages);
            }
          }}
        >
          {currentLanguage.toUpperCase()}
        </div>

        <div
          className="hamburger focus-highlight"
          tabIndex={0}
          onClick={toggleMobileMenu}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleMobileMenu();
            }
          }}
        >
          <span className={`line ${isMobileMenuOpen ? "open" : ""}`}></span>
          <span className={`line ${isMobileMenuOpen ? "open" : ""}`}></span>
          <span className={`line ${isMobileMenuOpen ? "open" : ""}`}></span>
        </div>
      </nav>
    </header>
  );
};

export default Header;
